// Pictures kept in the Studio's asset store (specs/012). A page names one as { id: 'local-<sha256>', mime, size }
// instead of holding its bytes; the bytes come from the store's public route once per session and are shared by
// every page that names them. A page keeps naming a picture this way when it is saved again, whichever page it
// was copied to: the ids known to be in the store are remembered for the whole session, not per page.
import { STORED_ASSET_ID, isStoredAsset } from './document.mjs';

const blobs = new Map();
const stored = new Set();
const route = id => `/api/studio/assets/${id}/content`;
let fetchBytes = id => fetch(route(id), { credentials: 'same-origin', redirect: 'error' });

const hex = buffer => Array.from(new Uint8Array(buffer), byte => byte.toString(16).padStart(2, '0')).join('');
const base64 = bytes => {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 32768) binary += String.fromCharCode(...bytes.subarray(i, i + 32768));
  return btoa(binary);
};

/** Whether the picture with this id is kept in the store, so that a saved page names it rather than holding it. */
export const isKnownStoredAsset = id => stored.has(id);

/** The bytes of a stored picture, fetched once per session; they must be the content its id is the hash of. */
export function storedAssetBlob(id) {
  if (typeof id !== 'string' || !STORED_ASSET_ID.test(id)) return Promise.reject(new Error(`자산 저장소 그림 ID가 아닙니다: ${id}`));
  if (!blobs.has(id)) {
    const loading = (async () => {
      let response;
      try { response = await fetchBytes(id); } catch { throw new Error(`자산 저장소에 연결하지 못해 그림을 불러오지 못했습니다: ${id}`); }
      if (!response.ok) throw new Error(`자산 저장소에서 그림을 불러오지 못했습니다 (${response.status}): ${id}`);
      const blob = await response.blob();
      if (`local-${hex(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer()))}` !== id) throw new Error(`자산 저장소가 다른 그림을 돌려주었습니다: ${id}`);
      stored.add(id);
      return blob;
    })();
    // A failed load is tried again the next time the picture is needed.
    loading.catch(() => { if (blobs.get(id) === loading) blobs.delete(id); });
    blobs.set(id, loading);
  }
  return blobs.get(id);
}

/** Bytes just put in the store (the Saju import): the pages built from them name them and need not fetch them back. */
export function rememberStoredAsset(id, blob) {
  if (!STORED_ASSET_ID.test(id)) throw new Error(`자산 저장소 그림 ID가 아닙니다: ${id}`);
  stored.add(id);
  blobs.set(id, Promise.resolve(blob));
}

/** A page's assets with stored pictures turned back into bytes, for what takes only bytes (Penpot's scene import). */
export async function embedStoredAssets(assets) {
  return Promise.all(assets.map(async asset => {
    if (!isStoredAsset(asset)) return asset;
    const blob = await storedAssetBlob(asset.id);
    return { id: asset.id, mime: blob.type, data: base64(new Uint8Array(await blob.arrayBuffer())) };
  }));
}

/** How the bytes are fetched (tests); forgets what was fetched or remembered. */
export function configureStoredAssets({ fetch: fetcher }) {
  fetchBytes = fetcher;
  blobs.clear();
  stored.clear();
}
